import { useEffect, useRef, useState } from 'react';
import { MediaPipeService } from '../services/mediapipe.service';
import { GestureClassifier } from '../services/tensorflow.service';
import { EmotionDetector } from '../services/emotion.service';
import { HandLandmarks, GestureResult, EmotionResult } from '../types';
import { useAppStore } from '../store/appStore';

export const useGestureRecognition = (videoElement: HTMLVideoElement | null) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const mediaPipeRef = useRef<MediaPipeService | null>(null);
  const classifierRef = useRef<GestureClassifier | null>(null);
  const emotionDetectorRef = useRef<EmotionDetector | null>(null);
  const { 
    setCurrentGesture, 
    addGestureToHistory, 
    setProcessing,
    setCurrentEmotion,
    customGestures,
    isTrainingMode
  } = useAppStore();
  const landmarkBufferRef = useRef<HandLandmarks[]>([]);

  useEffect(() => {
    const initialize = async () => {
      if (!videoElement) return;

      try {
        setProcessing(true);

        // Initialize gesture classifier
        classifierRef.current = new GestureClassifier();
        await classifierRef.current.loadModel();
        
        // Load custom gestures
        if (customGestures.length > 0) {
          classifierRef.current.setCustomGestures(customGestures);
        }

        // Initialize emotion detector
        emotionDetectorRef.current = new EmotionDetector();
        await emotionDetectorRef.current.initialize(videoElement, handleEmotion);

        // Initialize MediaPipe for hand tracking
        mediaPipeRef.current = new MediaPipeService();
        await mediaPipeRef.current.initialize(videoElement, handleLandmarks);

        setIsInitialized(true);
        setProcessing(false);
        
        // Start emotion detection loop
        startEmotionDetection(videoElement);
      } catch (error) {
        console.error('Failed to initialize gesture recognition:', error);
        setProcessing(false);
      }
    };

    initialize();

    return () => {
      if (mediaPipeRef.current) {
        mediaPipeRef.current.stop();
      }
      if (emotionDetectorRef.current) {
        emotionDetectorRef.current.stop();
      }
    };
  }, [videoElement]);

  // Update custom gestures when they change
  useEffect(() => {
    if (classifierRef.current && customGestures.length > 0) {
      classifierRef.current.setCustomGestures(customGestures);
      console.log('Updated custom gestures:', customGestures.length);
    }
  }, [customGestures]);

  const startEmotionDetection = (video: HTMLVideoElement) => {
    const detectEmotion = async () => {
      if (emotionDetectorRef.current && video) {
        await emotionDetectorRef.current.processFrame(video);
      }
      // Run emotion detection every 500ms
      setTimeout(detectEmotion, 500);
    };
    detectEmotion();
  };

  const handleEmotion = (emotion: EmotionResult) => {
    setCurrentEmotion(emotion);
  };

  const handleLandmarks = async (handLandmarks: HandLandmarks[]) => {
    console.log('Landmarks detected:', handLandmarks.length, 'hands');
    
    if (!classifierRef.current || handLandmarks.length === 0) {
      setCurrentGesture(null);
      return;
    }

    // Use first hand for classification
    const landmarks = handLandmarks[0].landmarks;

    // In training mode, capture landmarks for custom gesture training
    if (isTrainingMode) {
      // Landmarks will be captured by the CustomGestureTrainer component
      // through a global event or ref
      window.dispatchEvent(new CustomEvent('landmarksCaptured', { 
        detail: { landmarks } 
      }));
      return;
    }

    try {
      console.log('Classifying gesture with', landmarks.length, 'landmarks');
      
      const result = await classifierRef.current.predict(landmarks);
      console.log('Gesture detected:', result.gesture, 'confidence:', result.confidence);

      // Only update if confidence is high enough
      if (result.confidence > 0.7) {
        const gestureResult: GestureResult = {
          gesture: result.gesture,
          confidence: result.confidence,
          timestamp: Date.now()
        };

        setCurrentGesture(gestureResult);
        addGestureToHistory(gestureResult);
      }
    } catch (error) {
      console.error('Gesture prediction error:', error);
    }
  };

  const captureTrainingSample = () => {
    if (landmarkBufferRef.current.length > 0) {
      const sample = landmarkBufferRef.current[landmarkBufferRef.current.length - 1];
      return sample.landmarks;
    }
    return null;
  };

  return {
    isInitialized,
    captureTrainingSample
  };
};
